import { Transaction } from '../types';
import apiClient from './api';
import { mockFinancialOverview } from '../data/mockData';

export const transactionsService = {
  // Get all transactions for the current user
  async getTransactions(): Promise<Transaction[]> {
    try {
      const response = await apiClient.get('/transactions');

      return response.data.data || [];
    } catch (error) {
      console.warn('Failed to fetch transactions from API, using mock data:', error);
      // Return mock data as fallback - cast to correct type

      return (mockFinancialOverview.recentTransactions || []).map((t) => ({
        ...t,
        type: t.type as 'income' | 'expense',
      }));
    }
  },

  // Get transactions with filters
  async getTransactionsFiltered(filters: {
    category?: string;
    type?: 'income' | 'expense' | 'all';
    startDate?: string;
    endDate?: string;
    search?: string;
  }): Promise<Transaction[]> {
    try {
      const response = await apiClient.get('/transactions', { params: filters });

      return response.data.data || [];
    } catch (error) {
      console.warn('Failed to fetch filtered transactions from API, using mock data:', error);
      const mockTransactions = (mockFinancialOverview.recentTransactions || []).map((t) => ({
        ...t,
        type: t.type as 'income' | 'expense',
      }));

      // Apply filters to mock data
      let filtered = mockTransactions;

      if (filters.category && filters.category !== 'all') {
        filtered = filtered.filter((t) => t.category === filters.category);
      }

      if (filters.type && filters.type !== 'all') {
        filtered = filtered.filter((t) => t.type === filters.type);
      }

      if (filters.search) {
        const searchLower = filters.search.toLowerCase();

        filtered = filtered.filter(
          (t) => t.description.toLowerCase().includes(searchLower) || t.category.toLowerCase().includes(searchLower),
        );
      }

      if (filters.startDate) {
        filtered = filtered.filter((t) => new Date(t.date) >= new Date(filters.startDate!));
      }

      if (filters.endDate) {
        filtered = filtered.filter((t) => new Date(t.date) <= new Date(filters.endDate!));
      }

      return filtered;
    }
  },

  // Create a new transaction
  async createTransaction(transaction: Omit<Transaction, 'id'>): Promise<Transaction> {
    try {
      const response = await apiClient.post('/transactions', transaction);

      return response.data.data;
    } catch (error) {
      console.error('Failed to create transaction:', error);
      throw error;
    }
  },

  // Update an existing transaction
  async updateTransaction(id: string, transaction: Partial<Transaction>): Promise<Transaction> {
    try {
      const response = await apiClient.put(`/transactions/${id}`, transaction);

      return response.data.data;
    } catch (error) {
      console.error('Failed to update transaction:', error);
      throw error;
    }
  },

  // Delete a transaction
  async deleteTransaction(id: string): Promise<void> {
    try {
      await apiClient.delete(`/transactions/${id}`);
    } catch (error) {
      console.error('Failed to delete transaction:', error);
      throw error;
    }
  },

  // Get transaction categories
  async getCategories(): Promise<string[]> {
    try {
      const response = await apiClient.get('/transactions/categories');

      return response.data.data || [];
    } catch (error) {
      console.warn('Failed to fetch categories from API, using defaults:', error);

      return [
        'food',
        'transport',
        'entertainment',
        'shopping',
        'bills',
        'healthcare',
        'education',
        'income',
        'investment',
        'other',
      ];
    }
  },

  // Get transaction statistics
  async getTransactionStats(period: 'week' | 'month' | 'year' = 'month'): Promise<{
    totalIncome: number;
    totalExpenses: number;
    netIncome: number;
    transactionCount: number;
    topCategories: Array<{ category: string; amount: number; count: number }>;
  }> {
    try {
      const response = await apiClient.get(`/transactions/stats?period=${period}`);

      return response.data.data;
    } catch (error) {
      console.warn('Failed to fetch transaction stats from API, calculating from mock data:', error);
      const transactions = (mockFinancialOverview.recentTransactions || []).map((t) => ({
        ...t,
        type: t.type as 'income' | 'expense',
      }));

      const income = transactions.filter((t) => t.type === 'income');
      const expenses = transactions.filter((t) => t.type === 'expense');

      const totalIncome = income.reduce((sum, t) => sum + t.amount, 0);
      const totalExpenses = expenses.reduce((sum, t) => sum + Math.abs(t.amount), 0);

      // Calculate top categories
      const categoryTotals = transactions.reduce(
        (acc, t) => {
          if (!acc[t.category]) {
            acc[t.category] = { amount: 0, count: 0 };
          }
          acc[t.category].amount += Math.abs(t.amount);
          acc[t.category].count += 1;

          return acc;
        },
        {} as Record<string, { amount: number; count: number }>,
      );

      const topCategories = Object.entries(categoryTotals)
        .map(([category, data]) => ({ category, ...data }))
        .sort((a, b) => b.amount - a.amount)
        .slice(0, 5);

      return {
        totalIncome,
        totalExpenses,
        netIncome: totalIncome - totalExpenses,
        transactionCount: transactions.length,
        topCategories,
      };
    }
  },
};

export default transactionsService;
